import * as React from "react";
import useApi from "@/hooks/useApi";
import { Severity } from "@velaro/velaro-component-lib";
import useToastAlert from "@/hooks/useToastAlert";

interface QnAPair {
  id: string;
  question: string;
  answer: string;
}

interface BotStatus {
  vectorEnabled: boolean;
  enableKbBotIndexing: boolean;
  hasAI: boolean;
}

interface Props {
  shopifyDomain?: string;
}

export default function ShopifyBotKnowledge({ shopifyDomain }: Props) {
  const api = useApi();
  const toastAlert = useToastAlert();
  const [status, setStatus] = React.useState<BotStatus | null>(null);
  const [pairs, setPairs] = React.useState<QnAPair[]>([]);
  const [loading, setLoading] = React.useState(true);
  const [saving, setSaving] = React.useState(false);
  const [scanLoading, setScanLoading] = React.useState(false);
  const [scanDone, setScanDone] = React.useState(false);
  const [savedCount, setSavedCount] = React.useState<number | null>(null);
  const [lastSaved, setLastSaved] = React.useState<string | null>(null);
  const [fetchingPolicies, setFetchingPolicies] = React.useState(false);
  const [policyFetchResult, setPolicyFetchResult] = React.useState<
    null | { count: number } | { error: string }
  >(null);

  React.useEffect(() => {
    async function load() {
      setLoading(true);
      try {
        const [statusResp, qnaResp] = await Promise.all([
          api.admin.get("ShopifyBot/Status"),
          api.admin.get("ShopifyBot/SuggestedQnA"),
        ]);
        const statusData = await statusResp.json();
        const qnaData = await qnaResp.json();
        setStatus(statusData ?? { vectorEnabled: false, enableKbBotIndexing: false, hasAI: false });
        // Handle both old array format and new { templates, savedCount, lastSaved } format
        const templates = Array.isArray(qnaData) ? qnaData : (Array.isArray(qnaData?.templates) ? qnaData.templates : []);
        if (templates.length > 0) {
          setPairs(
            templates.map((t: any) => ({
              id: t.id ?? `pair_${Math.random()}`,
              question: t.question ?? "",
              answer: t.answer ?? "",
            }))
          );
        }
        if (!Array.isArray(qnaData) && qnaData?.savedCount != null) {
          setSavedCount(qnaData.savedCount);
          setLastSaved(qnaData.lastSaved ?? null);
        }
      } catch {
        setStatus({ vectorEnabled: false, enableKbBotIndexing: false, hasAI: false });
      } finally {
        setLoading(false);
      }
    }
    load();
  }, []);

  async function scanStore() {
    if (!shopifyDomain) {
      toastAlert.displayToast(Severity.Warning, "Connect your Shopify store first.");
      return;
    }
    setScanLoading(true);
    try {
      const resp = await api.admin.post("ScraperJobs/Create", {
        name: `Shopify store — ${shopifyDomain}`,
        url: `https://${shopifyDomain}`,
        maxPages: 100,
        followLinks: true,
      });
      const data = await resp.json();
      if (data.allowed === false) {
        toastAlert.displayToast(Severity.Warning, data.reason || "Scan limit reached.");
      } else if (data.job_id) {
        setScanDone(true);
        toastAlert.displayToast(Severity.Success, `Scanning ${shopifyDomain} — this takes 2–3 minutes.`);
      } else {
        toastAlert.displayToast(Severity.Error, data.error || "Failed to start scan.");
      }
    } catch {
      toastAlert.displayToast(Severity.Error, "Failed to start scan.");
    } finally {
      setScanLoading(false);
    }
  }

  async function saveToBot() {
    const filledPairs = pairs.filter((p) => p.answer.trim().length > 0);
    if (filledPairs.length === 0) {
      toastAlert.displayToast(Severity.Warning, "Fill in at least one answer before saving.");
      return;
    }
    setSaving(true);
    try {
      const resp = await api.admin.post(
        "ShopifyBot/PushQnA",
        filledPairs.map((p) => ({ question: p.question, answer: p.answer }))
      );
      const data = await resp.json();
      if (data.success) {
        setSavedCount(data.pushed ?? data.savedCount ?? null);
        setLastSaved(data.lastSaved ?? null);
        toastAlert.displayToast(Severity.Success, data.message || `${data.pushed} pairs saved to bot.`);
      } else {
        toastAlert.displayToast(Severity.Error, data.message || "Failed to save.");
      }
    } catch {
      toastAlert.displayToast(Severity.Error, "Failed to save to bot.");
    } finally {
      setSaving(false);
    }
  }

  async function fetchPolicies() {
    setFetchingPolicies(true);
    setPolicyFetchResult(null);
    try {
      const resp = await api.admin.get("ShopifyBot/FetchPolicies");
      const data = await resp.json();
      if (data.success && Array.isArray(data.pairs)) {
        let filled = 0;
        setPairs((prev) =>
          prev.map((existing) => {
            const match = (data.pairs as QnAPair[]).find((p) => p.id === existing.id);
            if (match && existing.answer.trim() === "" && match.answer.trim() !== "") {
              filled++;
              return { ...existing, answer: match.answer };
            }
            return existing;
          })
        );
        setPolicyFetchResult({ count: filled });
      } else {
        setPolicyFetchResult({ error: data.message || "Could not reach your store policies." });
      }
    } catch {
      setPolicyFetchResult({
        error: "Could not reach your store policies. Make sure your store is connected and try again.",
      });
    } finally {
      setFetchingPolicies(false);
    }
  }

  function addCustomPair() {
    setPairs((prev) => [...prev, { id: `custom_${Date.now()}`, question: "", answer: "" }]);
  }

  function removePair(id: string) {
    setPairs((prev) => prev.filter((p) => p.id !== id));
  }

  function updatePair(id: string, field: "question" | "answer", value: string) {
    setPairs((prev) => prev.map((p) => (p.id === id ? { ...p, [field]: value } : p)));
  }

  if (loading) {
    return (
      <div className="flex justify-center py-16">
        <div className="w-6 h-6 border-2 border-blue-500 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  const noAI = !status?.hasAI;
  const noVector = !status?.vectorEnabled;
  const filledCount = pairs.filter((p) => p.answer.trim().length > 0).length;

  return (
    <div className="space-y-4">

      {/* Header */}
      <div className="bg-white rounded-2xl shadow-md p-6">
        <div className="flex items-start justify-between">
          <div>
            <div className="font-semibold text-slate-800 text-lg flex items-center gap-2">
              Bot Knowledge
              {savedCount !== null && savedCount > 0 && (
                <span className="text-xs font-medium px-2 py-0.5 rounded-full bg-green-100 text-green-700">
                  {savedCount} pairs saved{lastSaved ? ` · ${new Date(lastSaved).toLocaleDateString()}` : ""}
                </span>
              )}
            </div>
            <p className="text-sm text-slate-500 mt-1">
              Teach your bot to answer the questions Shopify customers ask most. Fill in your
              store's answers and the bot will use them in every conversation — instantly, no
              workflow setup needed.
            </p>
          </div>
        </div>

        {noAI && (
          <div className="mt-4 p-3 bg-amber-50 border border-amber-200 rounded-lg text-xs text-amber-700">
            <strong>AI not enabled on your plan.</strong> Upgrade to enable the bot knowledge feature.
          </div>
        )}
        {!noAI && noVector && (
          <div className="mt-4 p-3 bg-amber-50 border border-amber-200 rounded-lg text-xs text-amber-700">
            <strong>Vector index not configured.</strong> Contact support to enable bot knowledge indexing.
          </div>
        )}
      </div>

      {/* Auto-fill from store */}
      <div className="bg-white rounded-2xl shadow-md p-5 flex flex-col sm:flex-row sm:items-center gap-4">
        <div className="flex-1 min-w-0">
          <div className="font-semibold text-slate-700 flex items-center gap-2">
            <span aria-hidden="true">🏪</span>
            Auto-fill from your store
          </div>
          <p className="text-sm text-slate-500 mt-0.5">
            Pull your return policy, shipping info, and terms directly from your Shopify store
            pages — no copy/paste needed.
          </p>
          {policyFetchResult !== null && "count" in policyFetchResult && (
            <p className="mt-1.5 text-sm text-green-700 font-medium">
              Filled {policyFetchResult.count} of {pairs.length} answers from your store policies.
            </p>
          )}
          {policyFetchResult !== null && "error" in policyFetchResult && (
            <p className="mt-1.5 text-sm text-amber-700 font-medium">
              {policyFetchResult.error}
            </p>
          )}
        </div>
        <button
          type="button"
          disabled={fetchingPolicies || !shopifyDomain}
          onClick={fetchPolicies}
          className={`flex-shrink-0 px-4 py-2 text-sm font-medium rounded-lg transition-colors ${
            fetchingPolicies || !shopifyDomain
              ? "bg-slate-100 text-slate-400 cursor-not-allowed"
              : "bg-slate-800 text-white hover:bg-slate-900"
          }`}
        >
          {fetchingPolicies ? "Fetching policies..." : "Fill from store →"}
        </button>
      </div>

      {/* Step 1 — Store Scan */}
      <div className="bg-white rounded-2xl shadow-md p-6">
        <div className="flex items-start justify-between">
          <div>
            <div className="font-semibold text-slate-700 flex items-center gap-2">
              <span className="w-6 h-6 rounded-full bg-blue-600 text-white text-xs flex items-center justify-center font-bold flex-shrink-0">
                1
              </span>
              Scan your store pages
            </div>
            <p className="text-sm text-slate-500 mt-1 ml-8">
              We'll crawl your Shopify store and add your pages to the bot's knowledge base.
              This covers products, policies, FAQs, and any content you've published. Takes 2–3
              minutes.{shopifyDomain && <> Scanning: <strong>{shopifyDomain}</strong>.</>}
            </p>
          </div>
          <button
            type="button"
            disabled={scanLoading || scanDone || !shopifyDomain}
            onClick={scanStore}
            className={`ml-4 flex-shrink-0 px-4 py-2 text-sm font-medium rounded-lg transition-colors ${
              scanDone
                ? "bg-green-100 text-green-700 cursor-default"
                : scanLoading
                ? "bg-slate-100 text-slate-400 cursor-not-allowed"
                : !shopifyDomain
                ? "bg-slate-100 text-slate-400 cursor-not-allowed"
                : "bg-blue-600 text-white hover:bg-blue-700"
            }`}
          >
            {scanDone ? "Scan started" : scanLoading ? "Starting..." : "Scan my store"}
          </button>
        </div>
        {scanDone && (
          <div className="mt-3 ml-8 p-2 bg-green-50 border border-green-200 rounded text-xs text-green-700">
            Scan running in the background. Go to <strong>Chatbot Training</strong> to see progress
            and add the pages to your index.
          </div>
        )}
        {!shopifyDomain && (
          <div className="mt-3 ml-8 text-xs text-slate-400">
            Connect your Shopify store above to enable scanning.
          </div>
        )}
      </div>

      {/* Step 2 — QnA Builder */}
      <div className="bg-white rounded-2xl shadow-md p-6">
        <div className="flex items-start justify-between mb-4">
          <div>
            <div className="font-semibold text-slate-700 flex items-center gap-2">
              <span className="w-6 h-6 rounded-full bg-blue-600 text-white text-xs flex items-center justify-center font-bold flex-shrink-0">
                2
              </span>
              Quick Q&amp;A — your store's most common questions
            </div>
            <p className="text-sm text-slate-500 mt-1 ml-8">
              Fill in your answers below. The bot will use these to answer customers instantly.
              Leave blank any questions that don't apply to your store.
            </p>
          </div>
          <div className="flex items-center gap-2 ml-4 flex-shrink-0">
            <span className="text-xs text-slate-400">
              {filledCount}/{pairs.length} answered
            </span>
            <button
              type="button"
              disabled={saving || noAI || noVector || filledCount === 0}
              onClick={saveToBot}
              className={`px-4 py-2 text-sm font-medium rounded-lg transition-colors ${
                saving || noAI || noVector || filledCount === 0
                  ? "bg-slate-100 text-slate-400 cursor-not-allowed"
                  : "bg-green-600 text-white hover:bg-green-700"
              }`}
            >
              {saving ? "Saving..." : "Save to bot"}
            </button>
          </div>
        </div>

        <div className="space-y-3 ml-8">
          {pairs.map((pair) => (
            <div key={pair.id} className="rounded-xl border border-slate-200 p-4">
              <div className="flex items-start gap-3">
                <div className="flex-1">
                  {pair.id.startsWith("custom_") ? (
                    <input
                      type="text"
                      value={pair.question}
                      onChange={(e) => updatePair(pair.id, "question", e.target.value)}
                      placeholder="Type your question..."
                      className="w-full text-sm font-medium text-slate-700 bg-transparent border-b border-slate-200 pb-1 mb-2 focus:outline-none focus:border-blue-400"
                    />
                  ) : (
                    <div className="text-sm font-medium text-slate-700 mb-2">{pair.question}</div>
                  )}
                  <textarea
                    rows={3}
                    value={pair.answer}
                    onChange={(e) => updatePair(pair.id, "answer", e.target.value)}
                    placeholder="Type your answer here — be specific about your store's policy..."
                    className="w-full text-sm text-slate-600 bg-slate-50 border border-slate-200 rounded-lg p-2 resize-none focus:outline-none focus:ring-2 focus:ring-blue-300 focus:border-blue-300"
                  />
                </div>
                {pair.id.startsWith("custom_") && (
                  <button
                    type="button"
                    onClick={() => removePair(pair.id)}
                    aria-label="Remove pair"
                    className="text-slate-300 hover:text-red-400 mt-1 flex-shrink-0"
                  >
                    <svg
                      viewBox="0 0 16 16"
                      width="14"
                      height="14"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="1.5"
                      strokeLinecap="round"
                      aria-hidden="true"
                    >
                      <path d="M4 4l8 8M12 4l-8 8" />
                    </svg>
                  </button>
                )}
              </div>
            </div>
          ))}

          <button
            type="button"
            onClick={addCustomPair}
            className="flex items-center gap-2 text-sm text-blue-600 hover:text-blue-700 font-medium py-1"
          >
            <svg
              viewBox="0 0 16 16"
              width="14"
              height="14"
              fill="none"
              stroke="currentColor"
              strokeWidth="1.5"
              strokeLinecap="round"
              aria-hidden="true"
            >
              <path d="M8 3v10M3 8h10" />
            </svg>
            Add custom question
          </button>
        </div>

        <div className="mt-4 ml-8 p-3 bg-blue-50 border border-blue-200 rounded-lg text-xs text-blue-700">
          <strong>Tip:</strong> The more specific your answers, the better. Instead of "See our
          website", say "We accept returns within 30 days of purchase. Items must be unused and in
          original packaging." Specific answers get much better bot retrieval scores.
        </div>
      </div>

      {/* Step 3 — Power Features */}
      <div className="bg-white rounded-2xl shadow-md p-6">
        <div className="font-semibold text-slate-700 flex items-center gap-2 mb-1">
          <span className="w-6 h-6 rounded-full bg-blue-600 text-white text-xs flex items-center justify-center font-bold flex-shrink-0">
            3
          </span>
          Power features — beyond basic Q&amp;A
        </div>
        <p className="text-sm text-slate-500 ml-8 mb-4">
          Your bot can go far beyond answering policy questions. These AI skills let it search your
          product catalog, compare items side-by-side, build custom quotes, and more — all in the
          chat window.
        </p>
        <div className="ml-8 grid grid-cols-1 sm:grid-cols-2 gap-3 mb-4">
          {[
            {
              label: "Product Search & Comparison",
              tool: "shopify_search_products",
              desc: "Visitor asks about products — bot searches your catalog, shows details and availability, compares options.",
              tier: "Professional",
              icon: (
                <svg viewBox="0 0 20 20" width="18" height="18" fill="none" stroke="currentColor" strokeWidth="1.5" aria-hidden="true">
                  <circle cx="9" cy="9" r="6" /><path d="M15 15l3 3" strokeLinecap="round" />
                </svg>
              ),
            },
            {
              label: "Quote Builder",
              tool: "shopify_create_quote",
              desc: "Visitor picks products — bot creates a Shopify draft order and sends a direct checkout link. Zero friction.",
              tier: "Professional",
              icon: (
                <svg viewBox="0 0 20 20" width="18" height="18" fill="none" stroke="currentColor" strokeWidth="1.5" aria-hidden="true">
                  <rect x="3" y="3" width="14" height="14" rx="2" /><path d="M7 10h6M7 13h4M7 7h6" strokeLinecap="round" />
                </svg>
              ),
            },
            {
              label: "Order Tracking",
              tool: "shopify_get_tracking",
              desc: "Visitor asks where their order is — bot fetches live tracking info and carrier link from Shopify.",
              tier: "Professional",
              icon: (
                <svg viewBox="0 0 20 20" width="18" height="18" fill="none" stroke="currentColor" strokeWidth="1.5" aria-hidden="true">
                  <path d="M3 10h14M10 3l7 7-7 7" strokeLinecap="round" strokeLinejoin="round" />
                </svg>
              ),
            },
            {
              label: "Self-Service Returns",
              tool: "shopify_initiate_return",
              desc: "Visitor requests a return — bot verifies the order and submits the return request directly in Shopify.",
              tier: "Professional",
              icon: (
                <svg viewBox="0 0 20 20" width="18" height="18" fill="none" stroke="currentColor" strokeWidth="1.5" aria-hidden="true">
                  <path d="M3 10a7 7 0 1 0 14 0H3M10 3l-4 4 4 4" strokeLinecap="round" strokeLinejoin="round" />
                </svg>
              ),
            },
            {
              label: "Abandoned Cart Recovery",
              tool: "shopify_get_abandoned_cart",
              desc: "Customer left items in their cart — bot finds the abandoned checkout and sends a recovery link to complete the purchase.",
              tier: "Professional",
              icon: (
                <svg viewBox="0 0 20 20" width="18" height="18" fill="none" stroke="currentColor" strokeWidth="1.5" aria-hidden="true">
                  <path d="M3 3h2l2.5 10h8l2-6H7.5" strokeLinecap="round" strokeLinejoin="round" />
                  <circle cx="9" cy="16" r="1" /><circle cx="15" cy="16" r="1" />
                </svg>
              ),
            },
            {
              label: "Coupon Code Validation",
              tool: "shopify_apply_coupon",
              desc: "Customer asks for a discount — bot validates the code in real time, shows the discount value and expiry, then shares it with instructions.",
              tier: "Professional",
              icon: (
                <svg viewBox="0 0 20 20" width="18" height="18" fill="none" stroke="currentColor" strokeWidth="1.5" aria-hidden="true">
                  <path d="M3 10h14M10 3v14" strokeLinecap="round" />
                  <rect x="6" y="6" width="8" height="8" rx="1" />
                </svg>
              ),
            },
            {
              label: "Live Stock Alert",
              tool: "shopify_check_stock",
              desc: "Bot checks real-time inventory — uses urgency language when stock is low ('Only 3 left!') to drive conversions.",
              tier: "Professional",
              icon: (
                <svg viewBox="0 0 20 20" width="18" height="18" fill="none" stroke="currentColor" strokeWidth="1.5" aria-hidden="true">
                  <path d="M10 3v3M10 14v3M3 10h3M14 10h3" strokeLinecap="round" />
                  <circle cx="10" cy="10" r="4" />
                </svg>
              ),
            },
            {
              label: "Product Reviews (Judge.me)",
              tool: "shopify_get_reviews",
              desc: "Bot fetches live star ratings and top review highlights from Judge.me — answers 'is this product good?' instantly without leaving the chat.",
              tier: "Professional",
              icon: (
                <svg viewBox="0 0 20 20" width="18" height="18" fill="none" stroke="currentColor" strokeWidth="1.5" aria-hidden="true">
                  <path d="M10 2l2.4 5h5.2l-4.2 3.1 1.6 5L10 12l-5 3.1 1.6-5L2.4 7h5.2z" strokeLinejoin="round" />
                </svg>
              ),
            },
          ].map((f, i) => (
            <div key={i} className="rounded-xl border border-slate-200 p-4 flex gap-3">
              <div className="text-blue-500 mt-0.5 flex-shrink-0">{f.icon}</div>
              <div>
                <div className="text-sm font-semibold text-slate-700 flex items-center gap-2">
                  {f.label}
                  <span className="text-xs font-medium px-1.5 py-0.5 rounded bg-amber-100 text-amber-700">
                    {f.tier}
                  </span>
                </div>
                <div className="text-xs text-slate-500 mt-0.5">{f.desc}</div>
                <div className="text-xs text-slate-400 mt-1 font-mono">{f.tool}</div>
              </div>
            </div>
          ))}
        </div>
        <div className="ml-8 p-3 bg-slate-50 border border-slate-200 rounded-lg text-xs text-slate-600">
          <strong>To enable:</strong> Go to <strong>Bots &rarr; AI Training &rarr; Skills</strong> and enable the
          Shopify Commerce skills. These require a Professional plan and a connected Shopify store.
          Then use the <strong>Launch Starter Wizard</strong> (Setup tab) to add a Shopify workflow.
        </div>
      </div>

      {/* Step 4 — Analytics */}
      <BotAnalyticsPanel api={api} />
    </div>
  );
}

function BotAnalyticsPanel({ api }: { api: ReturnType<typeof useApi> }) {
  const [summary, setSummary] = React.useState<{
    totalSkillCalls: number;
    errorCount: number;
    successRate: number;
    topSkill: string;
    topSkillCount: number;
    conversationsTrainedCount: number;
  } | null>(null);

  React.useEffect(() => {
    (async () => {
      try {
        const resp = await api.admin.get("BotAnalytics/Summary?days=30");
        const data = await resp.json();
        setSummary(data);
      } catch { /* ignore */ }
    })();
  }, []);

  if (!summary || summary.totalSkillCalls === 0) return null;

  return (
    <div className="rounded-2xl border border-slate-200 p-5 bg-white">
      <div className="flex items-center gap-2 mb-3">
        <svg viewBox="0 0 20 20" width="18" height="18" fill="none" stroke="currentColor" strokeWidth="1.5" aria-hidden="true" className="text-blue-500">
          <rect x="3" y="10" width="3" height="7" /><rect x="8.5" y="6" width="3" height="11" /><rect x="14" y="3" width="3" height="14" />
        </svg>
        <span className="text-sm font-semibold text-slate-700">Bot performance — last 30 days</span>
      </div>
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        {[
          { label: "Skill calls", value: summary.totalSkillCalls.toLocaleString() },
          { label: "Success rate", value: `${summary.successRate}%` },
          { label: "Top skill", value: summary.topSkill.replace("shopify_", "").replace(/_/g, " ") },
          { label: "Chats trained", value: summary.conversationsTrainedCount.toLocaleString() }
        ].map((stat, i) => (
          <div key={i} className="rounded-lg bg-slate-50 border border-slate-100 p-3 text-center">
            <div className="text-lg font-bold text-slate-800">{stat.value}</div>
            <div className="text-xs text-slate-400 mt-0.5">{stat.label}</div>
          </div>
        ))}
      </div>
    </div>
  );
}
